import * as THREE from 'three';
import { createBeybladeModel } from './BeybladeModel.js';
import { PARTS, computeStats, defaultBey } from './PartData.js';

// ── 3D Preview renderer with orbit camera controls ──
export class PreviewRenderer {
  constructor() {
    this.canvas = document.getElementById('preview-canvas');
    this.renderer = new THREE.WebGLRenderer({ canvas: this.canvas, antialias: true, alpha: true });
    this.renderer.setPixelRatio(window.devicePixelRatio);
    this.renderer.shadowMap.enabled = true;
    this.renderer.toneMapping = THREE.ACESFilmicToneMapping;

    this.scene = new THREE.Scene();
    this.camera = new THREE.PerspectiveCamera(40, 1, 0.1, 100);

    // Orbit camera state
    this.orbitRadius = 4.0;
    this.orbitTheta = Math.PI / 4;    // horizontal angle
    this.orbitPhi = Math.PI / 3.5;    // vertical angle (from top)
    this.lookAt = new THREE.Vector3(0, 0.15, 0);
    this.autoRotate = true;
    this.autoRotateSpeed = 0.005;
    this.isDragging = false;
    this.lastMouse = { x: 0, y: 0 };
    this.dragTimeout = null;

    this._updateCameraFromOrbit();

    // Lighting
    const amb = new THREE.AmbientLight(0x404060, 0.8);
    this.scene.add(amb);

    const key = new THREE.DirectionalLight(0xffffff, 1.5);
    key.position.set(3, 5, 3);
    key.castShadow = true;
    this.scene.add(key);

    const fill = new THREE.DirectionalLight(0x6366f1, 0.5);
    fill.position.set(-3, 2, -1);
    this.scene.add(fill);

    const rim = new THREE.DirectionalLight(0xa855f7, 0.4);
    rim.position.set(0, 1, -3);
    this.scene.add(rim);

    // Platform
    const platGeo = new THREE.CylinderGeometry(1.2, 1.2, 0.08, 32);
    const platMat = new THREE.MeshStandardMaterial({
      color: 0x1a1a2e, metalness: 0.8, roughness: 0.2,
    });
    const platform = new THREE.Mesh(platGeo, platMat);
    platform.position.y = -0.4;
    platform.receiveShadow = true;
    this.scene.add(platform);

    // Grid helper
    const grid = new THREE.GridHelper(2.4, 12, 0x333355, 0x222244);
    grid.position.y = -0.35;
    this.scene.add(grid);

    this.beyMesh = null;
    this.rotationY = 0;
    this.running = false;

    // ── Orbit controls (mouse + touch) ──
    this._onPointerDown = this._onPointerDown.bind(this);
    this._onPointerMove = this._onPointerMove.bind(this);
    this._onPointerUp = this._onPointerUp.bind(this);
    this._onWheel = this._onWheel.bind(this);

    this.canvas.addEventListener('pointerdown', this._onPointerDown);
    this.canvas.addEventListener('pointermove', this._onPointerMove);
    this.canvas.addEventListener('pointerup', this._onPointerUp);
    this.canvas.addEventListener('pointercancel', this._onPointerUp);
    this.canvas.addEventListener('wheel', this._onWheel, { passive: false });
    // prevent context menu on right-click
    this.canvas.addEventListener('contextmenu', e => e.preventDefault());
  }

  _updateCameraFromOrbit() {
    // Clamp phi to avoid flipping
    this.orbitPhi = Math.max(0.15, Math.min(Math.PI / 2 - 0.05, this.orbitPhi));
    this.orbitRadius = Math.max(2.0, Math.min(8.0, this.orbitRadius));

    const x = this.orbitRadius * Math.sin(this.orbitPhi) * Math.sin(this.orbitTheta);
    const y = this.orbitRadius * Math.cos(this.orbitPhi);
    const z = this.orbitRadius * Math.sin(this.orbitPhi) * Math.cos(this.orbitTheta);

    this.camera.position.set(
      this.lookAt.x + x,
      this.lookAt.y + y,
      this.lookAt.z + z
    );
    this.camera.lookAt(this.lookAt);
  }

  _onPointerDown(e) {
    this.isDragging = true;
    this.autoRotate = false;
    this.lastMouse.x = e.clientX;
    this.lastMouse.y = e.clientY;
    this.canvas.setPointerCapture(e.pointerId);

    // Resume auto-rotate after 3 seconds of no interaction
    clearTimeout(this.dragTimeout);
  }

  _onPointerMove(e) {
    if (!this.isDragging) return;

    const dx = e.clientX - this.lastMouse.x;
    const dy = e.clientY - this.lastMouse.y;
    this.lastMouse.x = e.clientX;
    this.lastMouse.y = e.clientY;

    // Horizontal drag = rotate theta
    this.orbitTheta -= dx * 0.008;
    // Vertical drag = rotate phi
    this.orbitPhi -= dy * 0.008;

    this._updateCameraFromOrbit();
  }

  _onPointerUp(e) {
    this.isDragging = false;
    // Resume auto-rotation after 3 seconds
    clearTimeout(this.dragTimeout);
    this.dragTimeout = setTimeout(() => {
      this.autoRotate = true;
    }, 3000);
  }

  _onWheel(e) {
    e.preventDefault();
    this.orbitRadius += e.deltaY * 0.005;
    this._updateCameraFromOrbit();
  }

  updateBey(config) {
    if (this.beyMesh) {
      this.scene.remove(this.beyMesh);
      this.beyMesh.traverse(child => {
        if (child.geometry) child.geometry.dispose();
        if (child.material) child.material.dispose();
      });
    }
    this.beyMesh = createBeybladeModel(config);
    this.beyMesh.position.y = 0.1;
    this.scene.add(this.beyMesh);
  }

  resize() {
    const parent = this.canvas.parentElement;
    if (!parent) return;
    const w = parent.clientWidth;
    const h = parent.clientHeight;
    this.renderer.setSize(w, h);
    this.camera.aspect = w / h;
    this.camera.updateProjectionMatrix();
  }

  start() {
    this.running = true;
    this.resize();
    window.addEventListener('resize', () => this.resize());
    this._loop();
  }

  stop() {
    this.running = false;
  }

  _loop() {
    if (!this.running) return;
    requestAnimationFrame(() => this._loop());

    // Auto-rotate the camera orbit
    if (this.autoRotate) {
      this.orbitTheta += this.autoRotateSpeed;
      this._updateCameraFromOrbit();
    }

    // Slow bey spin
    this.rotationY += 0.01;
    if (this.beyMesh) {
      this.beyMesh.rotation.y = this.rotationY;
    }

    this.renderer.render(this.scene, this.camera);
  }
}
